import { useState } from "react";

import { useIsMobile } from "../hooks";
import { ChevronRightIcon, QRCodeIcon } from "../Icons/index";
import {
  QRCodeModal,
  type QRCodeModalTranslations,
} from "../ReferralLinkModals/QRCodeModal";
import { Toggle } from "../Toggle";
import { Typo } from "../Typo";
import { CopyableLink } from "./CopyableLink";
import { ReferralLinkWidgetError } from "./ReferralLinkWidgetError";
import { ReferralLinkWidgetSkeleton } from "./ReferralLinkWidgetSkeleton";

export type ReferralLinkWidgetTranslations = {
  title: string;
  viewAllLinks: string;
  longLink: string;
  viewQRCode: string;
  errorMessage: string;
  refreshButton: string;
  qrCodeModal: QRCodeModalTranslations;
};

export type ReferralLinkWidgetProps = {
  referralUrl: string;
  shortReferralUrl?: string;
  isLoading?: boolean;
  isError?: boolean;
  onRefresh?: () => void;
  onViewAllLinks: () => void;
  onCopyLink?: (source: "link" | "icon") => void;
  onToggleLink?: () => void;
  onViewQRCode?: () => void;
  onDownloadQRCode?: () => void;
  translatedContent: ReferralLinkWidgetTranslations;
  isMobile?: boolean;
};

export const ReferralLinkWidget = ({
  referralUrl,
  shortReferralUrl,
  isLoading,
  isError,
  onRefresh,
  onViewAllLinks,
  onCopyLink,
  onToggleLink,
  onViewQRCode,
  onDownloadQRCode,
  translatedContent,
  isMobile: isMobileProp,
}: ReferralLinkWidgetProps) => {
  const isMobileHook = useIsMobile();
  const isMobile = isMobileProp ?? isMobileHook;
  const [isLongLink, setIsLongLink] = useState(true);
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);

  const handleToggleLongLink = (checked: boolean) => {
    setIsLongLink(checked);
    onToggleLink?.();
  };

  const handleViewQRCode = () => {
    setIsQRModalOpen(true);
    onViewQRCode?.();
  };

  const displayUrl =
    isLongLink || !shortReferralUrl ? referralUrl : shortReferralUrl;

  if (isLoading) {
    return <ReferralLinkWidgetSkeleton />;
  }

  return (
    <div
      id="referral-link-card"
      className="rounded-2xl border border-stroke-primary bg-surface-base-background p-6 shadow-sm lg:p-8"
    >
      <div className="flex items-center justify-between">
        <Typo
          type={isMobile ? "labelMD" : "labelLG"}
          textColor="text-text-primary"
        >
          {translatedContent.title}
        </Typo>
        <button
          type="button"
          className="flex items-center gap-1 lg:gap-2"
          onClick={onViewAllLinks}
          data-testid="view-all-links"
        >
          <Typo element="span" type="labelMD" smType="labelXS">
            {translatedContent.viewAllLinks}
          </Typo>
          <ChevronRightIcon className="rtl:rotate-180" />
        </button>
      </div>

      {isError ? (
        <ReferralLinkWidgetError
          errorMessage={translatedContent.errorMessage}
          refreshButton={translatedContent.refreshButton}
          onRefresh={onRefresh}
        />
      ) : (
        <>
          <div className="flex justify-center py-8 lg:py-10">
            <CopyableLink url={displayUrl} onCopy={onCopyLink} />
          </div>

          <div className="flex justify-between lg:justify-center lg:gap-6">
            <Toggle
              checked={isLongLink}
              onChange={handleToggleLongLink}
              label={translatedContent.longLink}
              data-testid="long-link-toggle"
            />
            <button
              type="button"
              className="flex items-center gap-2 rounded-lg border border-stroke-primary px-3 py-2"
              onClick={handleViewQRCode}
              data-testid="view-qr-code"
            >
              <QRCodeIcon />
              <Typo type="labelSM" smType="labelXS">
                {translatedContent.viewQRCode}
              </Typo>
            </button>
          </div>
        </>
      )}

      <QRCodeModal
        isOpen={isQRModalOpen}
        onClose={() => setIsQRModalOpen(false)}
        url={displayUrl}
        translations={translatedContent.qrCodeModal}
        onDownload={onDownloadQRCode}
        isMobile={isMobile}
      />
    </div>
  );
};
